import { __ } from "@wordpress/i18n";
import { InspectorControls, PanelColorSettings } from "@wordpress/block-editor";
import {
  PanelBody,
  ToggleControl,
  RangeControl,
  BaseControl,
  Button,
  ButtonGroup,
  __experimentalUnitControl as UnitControl,
  __experimentalBoxControl as BoxControl,
  __experimentalBorderControl as BorderControl,
} from "@wordpress/components";
import ShadowPicker from "../../../../../../Externals/Src/Gutenberg/CustomBoxShadow/ShadowComponent";

import TriggerSettings from "./trigger-settings";
import ModalSettings from "./modal-settings";
import AnimationSettings from "./animation-settings";
import "./style.scss";

export default function ModalInspectorControls({ attributes, setAttributes }) {
  const {
    backdropColor = "#00000080",
    enableCloseIcon = true,
    closeOnBackdropClick = true,
    closeOnEsc = true,
    popupStyles = {},
    closeStyles = {},
  } = attributes;

  const closeIconPositionOptions = [
    { label: "Top Right", value: "top-right" },
    { label: "Top Left", value: "top-left" },
  ];

  const handleMaxHeightChange = (value) => {
    const numericValue = parseFloat(value);
    const unit = value.replace(/[\d.-]/g, "") || "px";

    let newValue;
    if (isNaN(numericValue)) {
      if (unit === "%") newValue = 100;
      else if (unit === "vh") newValue = 90;
      else newValue = popupStyles.modalMaxHeight || 90;
    } else {
      newValue = numericValue;
    }

    setAttributes({
      popupStyles: {
        ...popupStyles,
        modalMaxHeight: newValue,
        modalMaxHeightUnit: unit,
      },
    });
  };

  const handleBorderChange = (newBorder) => {
    setAttributes({
      popupStyles: {
        ...popupStyles,
        borderWidth: newBorder?.width || "0px",
        borderColor: newBorder?.color || "#000000",
        borderStyle: newBorder?.style || "solid",
      },
    });
  };

  const handlePaddingChange = (newPadding) => {
    setAttributes({
      popupStyles: {
        ...popupStyles,
        padding: {
          top: newPadding.top || "20px",
          right: newPadding.right || "20px",
          bottom: newPadding.bottom || "20px",
          left: newPadding.left || "20px",
        },
      },
    });
  };

  return (
    <>
      <InspectorControls group="settings">
        <TriggerSettings
          attributes={attributes}
          setAttributes={setAttributes}
        />
        <ModalSettings
          attributes={attributes}
          setAttributes={setAttributes}
        />

        <PanelBody
          title={__("Close", "brandy-blocks")}
          initialOpen={true}
        >
          <ToggleControl
            label={__("Enable Close Icon", "brandy-blocks")}
            checked={enableCloseIcon}
            onChange={(value) => setAttributes({ enableCloseIcon: value })}
          />
          <ToggleControl
            label={__("Close on Backdrop Click", "brandy-blocks")}
            checked={closeOnBackdropClick}
            onChange={(value) =>
              setAttributes({ closeOnBackdropClick: value })
            }
          />
          <ToggleControl
            label={__("Close on Esc", "brandy-blocks")}
            checked={closeOnEsc}
            onChange={(value) => setAttributes({ closeOnEsc: value })}
          />
        </PanelBody>
      </InspectorControls>

      <InspectorControls group="styles">
        <AnimationSettings
          attributes={attributes}
          setAttributes={setAttributes}
        />

        <PanelBody
          title={__("Popup Styles", "brandy-blocks")}
          initialOpen={false}
        >
          <div className="brandy-modal-settings">
            <PanelColorSettings
              title={__("Backdrop Setting", "brandy-blocks")}
              initialOpen={false}
              enableAlpha={true}
              colorSettings={[
                {
                  value: backdropColor,
                  onChange: (newColor) =>
                    setAttributes({ backdropColor: newColor }),
                  label: __("Color", "brandy-blocks"),
                },
              ]}
            />
          </div>

          <div className="brandy-modal-settings">
            <UnitControl
              label={__("Modal Width", "brandy-blocks")}
              value={`${popupStyles.modalWidth || 600}${
                popupStyles.modalWidthUnit || "px"
              }`}
              onChange={(value) => {
                const numericValue = parseFloat(value);
                const unit = value.replace(/[\d.-]/g, "") || "px";
                let defaultValue = 600;
                if (unit === "%") defaultValue = 80;
                if (unit === "vw") defaultValue = 80;
                setAttributes({
                  popupStyles: {
                    ...popupStyles,
                    modalWidth: isNaN(numericValue)
                      ? defaultValue
                      : numericValue,
                    modalWidthUnit: unit,
                  },
                });
              }}
              units={[
                { value: "px", label: "px", default: 600 },
                { value: "%", label: "%", default: 80 },
                { value: "vw", label: "vw", default: 80 },
              ]}
            />
          </div>
          <div className="brandy-modal-settings">
            <UnitControl
              label={__("Modal Max Height", "brandy-blocks")}
              value={`${popupStyles.modalMaxHeight || 90}${
                popupStyles.modalMaxHeightUnit || "vh"
              }`}
              onChange={handleMaxHeightChange}
              units={[
                { value: "px", label: "px", default: 350 },
                { value: "%", label: "%", default: 100 },
                { value: "vh", label: "vh", default: 90 },
              ]}
            />
          </div>

          <div className="brandy-modal-padding-container-settings">
            <BoxControl
              label={__("Padding", "brandy-blocks")}
              values={{
                top: popupStyles.padding?.top || "20px",
                right: popupStyles.padding?.right || "20px",
                bottom: popupStyles.padding?.bottom || "20px",
                left: popupStyles.padding?.left || "20px",
              }}
              onChange={handlePaddingChange}
              units={[
                { value: "px", label: "px", default: 20 },
                { value: "%", label: "%", default: 5 },
              ]}
              allowLink={true}
              className="brandy-box-control"
            />
          </div>

          <div className="brandy-modal-settings">
            <BorderControl
              label={__("Border", "brandy-blocks")}
              onChange={handleBorderChange}
              value={{
                width: popupStyles.borderWidth || "0px",
                color: popupStyles.borderColor || "#000000",
                style: popupStyles.borderStyle || "solid",
              }}
              withSlider={true}
              colors={[]}
              enableStyle={true}
              className="brandy-border-control"
            />
          </div>
          <div className="brandy-modal-settings">
            <ShadowPicker
              label={__("Box Shadow", "brandy-blocks")}
              shadow={popupStyles.shadow}
              onChange={(value) => {
                setAttributes({
                  popupStyles: { ...popupStyles, shadow: value },
                });
              }}
            />
          </div>
        </PanelBody>

        <PanelBody
          title={__("Close Styles", "brandy-blocks")}
          initialOpen={false}
        >
          <div className="brandy-modal-settings">
            <RangeControl
              label={__("Icon Size", "brandy-blocks")}
              value={closeStyles.closeIconSize || 24}
              onChange={(value) =>
                setAttributes({
                  closeStyles: { ...closeStyles, closeIconSize: value },
                })
              }
              min={10}
              max={50}
            />
          </div>
          <div className="brandy-modal-settings">
            <BaseControl label={__("Icon Position", "brandy-blocks")}>
              <ButtonGroup className="brandy-position-buttons">
                {closeIconPositionOptions.map((option) => (
                  <Button
                    key={option.value}
                    variant={
                      closeStyles.closeIconPosition === option.value ? "primary" : undefined
                    }
                    onClick={() =>
                      setAttributes({
                        closeStyles: {
                          ...closeStyles,
                          closeIconPosition: option.value,
                        },
                      })
                    }
                    className="brandy-position-button"
                  >
                    {option.label}
                  </Button>
                ))}
              </ButtonGroup>
            </BaseControl>
          </div>
          <div className="brandy-modal-settings">
            <PanelColorSettings
              title={__("Icon Color", "brandy-blocks")}
              initialOpen={false}
              colorSettings={[
                {
                  value: closeStyles.closeIconColor || "#ffffff",
                  onChange: (newColor) =>
                    setAttributes({
                      closeStyles: {
                        ...closeStyles,
                        closeIconColor: newColor,
                      },
                    }),
                  label: __("Icon Color", "brandy-blocks"),
                },
              ]}
            />
          </div>
        </PanelBody>
      </InspectorControls>
    </>
  );
}
